/** Name der lokalen Datenbank der Station eines Basars (Warteschlange der Rückgabe-Scans, zwischengespeicherte Daten). */
export const stationDbName = (eventId: string): string => `station-${eventId}`;

/**
 * Ist die Station dieses Basars auf diesem Gerät schon eingerichtet (lokale Datenbank vorhanden)? Nur lesend, es wird nichts
 * angelegt. Ohne Auflistung der Datenbanken (älterer Browser) oder bei Fehlern: `false`.
 */
export async function stationExistsOnDevice(eventId: string): Promise<boolean> {
  try {
    if (typeof indexedDB.databases !== 'function') return false;
    const list = await indexedDB.databases();
    return list.some((entry) => entry.name === stationDbName(eventId));
  } catch {
    return false;
  }
}

/** Wartende Rückgabe-Scans einer Station dieses Geräts. */
export interface QueuedStation {
  eventId: string;
  count: number;
}

const STATION_PREFIX = 'station-';

/** Zählt die Einträge der Warteschlange einer Station nur lesend (nichts wird angelegt oder verändert). */
function countOutbox(name: string): Promise<number> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(name);
    } catch {
      resolve(0);
      return;
    }
    request.onupgradeneeded = () => {
      // Gibt es die Datenbank nicht, wird das Anlegen abgebrochen
      request.transaction?.abort();
    };
    request.onerror = (event) => {
      event.preventDefault();
      resolve(0);
    };
    request.onblocked = () => {
      resolve(0);
    };
    request.onsuccess = () => {
      const db = request.result;
      try {
        if (!db.objectStoreNames.contains('outbox')) {
          db.close();
          resolve(0);
          return;
        }
        const count = db.transaction(['outbox'], 'readonly').objectStore('outbox').count();
        count.onsuccess = () => {
          db.close();
          resolve(count.result);
        };
        count.onerror = () => {
          db.close();
          resolve(0);
        };
      } catch {
        db.close();
        resolve(0);
      }
    };
  });
}

/** Wartende Rückgabe-Scans je Station dieses Geräts (nur Stationen mit mindestens einem Eintrag). */
export async function countQueuedStationScans(): Promise<QueuedStation[]> {
  try {
    if (typeof indexedDB.databases !== 'function') return [];
    const list = await indexedDB.databases();
    const result: QueuedStation[] = [];
    for (const entry of list) {
      const name = entry.name;
      if (name === undefined || !name.startsWith(STATION_PREFIX)) continue;
      const count = await countOutbox(name);
      if (count > 0) result.push({ eventId: name.slice(STATION_PREFIX.length), count });
    }
    return result;
  } catch {
    return [];
  }
}

/** Zwischengespeicherte Körbe der Rückgabe (O4): Nummern und Artikel der Verkäufer, siehe `pages/returnBasket.tsx`. */
const isBasketKey = (key: IDBValidKey): boolean =>
  typeof key === 'string' && (key === 'baskets' || key.startsWith('basket:'));

/** Löscht die Körbe einer Station (Warteschlange, Endzeit und Einstellungen bleiben). Legt nichts an. */
function clearBaskets(name: string): Promise<void> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(name);
    } catch {
      resolve();
      return;
    }
    request.onupgradeneeded = () => {
      request.transaction?.abort();
    };
    request.onerror = (event) => {
      event.preventDefault();
      resolve();
    };
    request.onblocked = () => {
      resolve();
    };
    request.onsuccess = () => {
      const db = request.result;
      try {
        if (!db.objectStoreNames.contains('meta')) {
          db.close();
          resolve();
          return;
        }
        const transaction = db.transaction(['meta'], 'readwrite');
        const meta = transaction.objectStore('meta');
        const keys = meta.getAllKeys();
        keys.onsuccess = () => {
          for (const key of keys.result) if (isBasketKey(key)) meta.delete(key);
        };
        transaction.oncomplete = () => {
          db.close();
          resolve();
        };
        transaction.onerror = () => {
          db.close();
          resolve();
        };
        transaction.onabort = () => {
          db.close();
          resolve();
        };
      } catch {
        db.close();
        resolve();
      }
    };
  });
}

/**
 * Beim Abmelden: Die Körbe der Stationen (Verkäufernummern, Artikel) gehören nicht der nächsten Person am Gerät. Ohne Netz
 * öffnet die Rückgabe-Station nur mit ihren lokalen Daten (`OfflineGate`); nach einer Abmeldung soll dort nichts mehr zu sehen
 * sein. Die Warteschlange der Scans bleibt (sie ist schon erfasst).
 */
export async function clearStationCaches(): Promise<void> {
  try {
    if (typeof indexedDB.databases !== 'function') return;
    for (const entry of await indexedDB.databases()) {
      if (entry.name?.startsWith(STATION_PREFIX) === true) await clearBaskets(entry.name);
    }
  } catch {
    // ohne Speicher gibt es nichts zu löschen
  }
}
